import React, { useCallback, useState } from "react";
import {
	Button,
	Checkbox,
	Group,
	List,
	Loader,
	Modal,
	Progress,
	Stack,
	Text,
	ThemeIcon,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

import {
	startS3TenantWipe,
	startDatabaseWipeForFirm,
	startSearchIndexDeleteForFirm,
} from "../../actions/adminActions";
import { pollAdminJobToCompletion } from "../../actions/jobProgressActions";

// The single unified place a tenant's data gets permanently wiped from -
// replaces the old separate "Permanently Wipe Tenant" (S3), "Wipe Database
// for Firm", and "Delete Search Index for Firm" buttons, each of which used
// to drive its own confirmation dialog and job-progress snackbar.
const WIPE_STEPS = [
	{
		key: "s3",
		checkboxLabel: "All data in S3",
		// Shown while awaiting the initial API response (this is where the
		// backend checks the tenant is actually safe to wipe), before there's
		// an actual job to report progress on.
		setupLabel: "Setting up permanent wipe",
		progressLabel: "Preparing all S3 Objects for Permanent Deletion",
		run: (tenant, onProgress, onSetupComplete) =>
			startS3TenantWipe(tenant.id, tenant.expectedS3ObjectCount).then(
				(res) => {
					onSetupComplete && onSetupComplete();
					return res?.mysql?.jobID
						? pollAdminJobToCompletion(res.mysql.jobID, onProgress)
						: null;
				}
			),
	},
	{
		key: "db",
		checkboxLabel: "Database Records",
		progressLabel: "Wiping database records",
		run: (tenant, onProgress) =>
			startDatabaseWipeForFirm(tenant.id).then((res) =>
				res?.mysql?.jobID
					? pollAdminJobToCompletion(res.mysql.jobID, onProgress)
					: null
			),
	},
	{
		key: "search",
		checkboxLabel: "Search Index",
		progressLabel: "Deleting search index",
		run: (tenant) => startSearchIndexDeleteForFirm(tenant.id),
	},
];

const defaultSelection = { s3: true, db: true, search: true };

function WipeTenantDialog({ tenant, onClose, onComplete }) {
	const [selection, setSelection] = useState(defaultSelection);
	const [phase, setPhase] = useState("select"); // select | running | done
	const [stepIndex, setStepIndex] = useState(0);
	const [percentDone, setPercentDone] = useState(null);
	const [scanCount, setScanCount] = useState(null);
	const [expectedCount, setExpectedCount] = useState(null);
	const [currentTable, setCurrentTable] = useState(null);
	const [recordsDeleted, setRecordsDeleted] = useState(null);
	const [results, setResults] = useState([]);
	const [settingUp, setSettingUp] = useState(false);

	const opened = !!tenant;
	const running = phase === "running";
	const selectedSteps = WIPE_STEPS.filter((step) => selection[step.key]);

	const resetAndClose = useCallback(() => {
		setSelection(defaultSelection);
		setPhase("select");
		setStepIndex(0);
		setPercentDone(null);
		setScanCount(null);
		setExpectedCount(null);
		setCurrentTable(null);
		setRecordsDeleted(null);
		setResults([]);
		setSettingUp(false);
		onClose();
	}, [onClose]);

	const handleProgress = useCallback((percent, jobData) => {
		// percent is meaningless without a known expectedObjects
		setPercentDone(jobData?.expectedObjects ? percent : null);
		setScanCount(jobData?.totalVersionsSoFar ?? null);
		setExpectedCount(jobData?.expectedObjects ?? null);
		setCurrentTable(jobData?.currentTable ?? null);
		setRecordsDeleted(jobData?.totalRecordsDeletedSoFar ?? null);
	}, []);

	const handleConfirm = useCallback(async () => {
		setPhase("running");
		for (let i = 0; i < selectedSteps.length; i++) {
			setStepIndex(i);
			setPercentDone(null);
			setScanCount(null);
			setExpectedCount(null);
			setCurrentTable(null);
			setRecordsDeleted(null);
			const step = selectedSteps[i];
			setSettingUp(!!step.setupLabel);
			try {
				await step.run(tenant, handleProgress, () =>
					setSettingUp(false)
				);
			} catch (err) {
				// The API layer already surfaces a snackbar explaining why
				// (see errorActions.js). Later steps have no guard of their
				// own against wiping a tenant that isn't actually safe to
				// wipe, so stop here rather than continuing on to them.
				console.error(err);
				onComplete && onComplete();
				resetAndClose();
				return;
			}
		}
		setResults(
			selectedSteps.map((step) => ({
				key: step.key,
				label: step.checkboxLabel,
			}))
		);
		setPhase("done");
		onComplete && onComplete();
	}, [selectedSteps, tenant, onComplete, resetAndClose, handleProgress]);

	return (
		<Modal
			opened={opened}
			onClose={running ? () => {} : resetAndClose}
			title={tenant ? `Wipe Tenant: ${tenant.name}` : ""}
			size="md"
			closeOnClickOutside={!running}
			closeOnEscape={!running}
			withCloseButton={!running}
		>
			{tenant && phase === "select" ? (
				<Stack>
					<Text>
						This will permanently wipe the selected data below for
						this tenant. This cannot be undone.
					</Text>
					<Stack gap={8}>
						{WIPE_STEPS.map((step) => (
							<Checkbox
								key={step.key}
								label={step.checkboxLabel}
								checked={!!selection[step.key]}
								onChange={(event) => {
									const checked = event.currentTarget.checked;
									setSelection((current) => ({
										...current,
										[step.key]: checked,
									}));
								}}
							/>
						))}
					</Stack>
					<Group justify="flex-end">
						<Button variant="default" onClick={resetAndClose}>
							Cancel
						</Button>
						<Button
							color="red"
							disabled={selectedSteps.length === 0}
							onClick={handleConfirm}
						>
							Wipe Tenant
						</Button>
					</Group>
				</Stack>
			) : null}
			{tenant && running ? (
				<Stack py="md">
					<Text fw={700} ta="center">
						{settingUp ? (
							<>
								Step 0 of {selectedSteps.length}:{" "}
								{selectedSteps[stepIndex].setupLabel}...
							</>
						) : (
							<>
								Step {stepIndex + 1} of {selectedSteps.length}:{" "}
								{selectedSteps[stepIndex].progressLabel}...
							</>
						)}
					</Text>
					{percentDone != null ? (
						<Stack gap={4}>
							<Progress value={percentDone} size="lg" />
							<Text size="sm" c="dimmed" ta="center">
								{percentDone}% complete
							</Text>
						</Stack>
					) : (
						<Group justify="center">
							<Loader size="sm" />
						</Group>
					)}
					{scanCount != null &&
					selectedSteps[stepIndex]?.key === "s3" ? (
						<Text size="xs" c="dimmed" ta="center">
							{scanCount.toLocaleString()} objects found so far
							{expectedCount != null
								? ` (~${expectedCount.toLocaleString()} expected)`
								: ""}
						</Text>
					) : null}
					{recordsDeleted != null &&
					selectedSteps[stepIndex]?.key === "db" ? (
						<Text size="xs" c="dimmed" ta="center">
							{recordsDeleted.toLocaleString()} records deleted so
							far
							{currentTable ? ` (on ${currentTable})` : ""}
						</Text>
					) : null}
				</Stack>
			) : null}
			{tenant && phase === "done" ? (
				<Stack>
					<List spacing="xs">
						{results.map((result) => (
							<List.Item
								key={result.key}
								icon={
									<ThemeIcon
										color="green"
										size={20}
										radius="xl"
									>
										<IconCheck size={14} />
									</ThemeIcon>
								}
							>
								{result.label}: Complete
							</List.Item>
						))}
					</List>
					<Group justify="flex-end">
						<Button onClick={resetAndClose}>Close</Button>
					</Group>
				</Stack>
			) : null}
		</Modal>
	);
}

export default WipeTenantDialog;
